/// <amd-module name="TFS/Admin/Groups/Pivots/Settings/Components/SettingsPivotInitializer" />

import * as Constants from "TFS/Admin/Groups/Common/Constants";
import * as GroupPageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as React from "react";
import * as Resources from "TFS/Admin/Groups/Pivots/Settings/Resources";

import { IGroupsViewState, PermissionsGroup, TeamAdmin } from "TFS/Admin/Groups/Common/Contracts";
import { GroupNameMaxChar, IReservedGroupNames, teamImageUrl } from "TFS/Admin/Groups/Common/Constants";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import {
    getCurrentGroupAvatarUrl,
    getDeletedGroup,
    getGroupDescription,
    getGroupDisplayName,
    getIsDirty,
    getIsSaving,
    getMessageCardSeverity,
    getMessageCardText,
    getShowChangeImageDialog,
    getTeamAdmins,
    showAddAdminPanel
} from "TFS/Admin/Groups/Pivots/Settings/Redux/SettingsPivotSelectors";

import { AvatarSize } from "TFS/Admin/Common/Contracts/VSS.Profile";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { FormItem } from "VSSUI/FormItem";
import { GraphGroup } from "VSS/Platform/Contracts/Graph";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import { filterXSS } from "xss";
import { xssStyleDefaultOptions } from "xss/PresetOptions";
import { IIdentity } from "VSSUI/IdentityPicker";
import { IVssPageContext } from "VSS/Platform/Context";
import { ImageData } from "TFS/Admin/Common/Contracts/AvatarImagePicker.types";
import { ObservableValue } from "VSS/Core/Observable";
import { SettingsAdminsCard } from "TFS/Admin/Groups/Pivots/Settings/Components/SettingsAdminsCard";
import { SettingsDeleteCard } from "TFS/Admin/Groups/Pivots/Settings/Components/SettingsDeleteCard";
import { SettingsGroupImageCard } from "./SettingsGroupImageCard";
import { SettingsPivotActions } from "TFS/Admin/Groups/Pivots/Settings/Redux/SettingsPivotActions";
import { TeamMember } from "VSS/Platform/Contracts/WebApi";
import { TextField } from "VSSUI/TextField";
import { VssComponent } from "VSS/Platform/Layout";
import { connect } from "react-redux";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { format } from "VSS/Core/Util/String";
import { getAssetLocation } from "VSS/Platform/Location";
import { onClickFPS } from "VSS/Platform/FPS";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";

interface ISettingsPivotContainerProps {
    pageContext: IVssPageContext;
}

interface ISettingsPivotInitializerOwnProps extends ISettingsPivotContainerProps {
    groupDisplayName: string;
    groupDescription: string;
    selectedGroup?: PermissionsGroup;
    isSaving: boolean;
    showChangeImageDialog: boolean;
    currentGroupAvatarUrl: string;
    editGroupValue?: PermissionsGroup;
    isDirty: boolean;
    messageCardText: string;
    messageCardSeverity: MessageCardSeverity;
    deletedGroup?: GraphGroup;
    isTeamsPage?: boolean;
    teamAdmins: TeamAdmin[];
    showAddAdminPanel: boolean;
}

export type ISettingsPivotInitializerProps = ISettingsPivotInitializerOwnProps & typeof ActionsToDispatch;

export class SettingsPivotInitializer extends VssComponent<ISettingsPivotInitializerProps> {
    private newGroupNameErrorMessage = new ObservableValue<string>("");
    private _url: string;

    constructor(props: ISettingsPivotInitializerProps) {
        super(props);

        this.props.initView(this.props.selectedGroup, this.props.isTeamsPage);
        this._url = window.location.origin.toString() + window.location.pathname.toString() + Constants.DeletedGroupSufix;
    }

    public componentDidMount(): void {
        this.props.logTelemetry(GroupsComponent.SettingsPivot, "Page loaded", {}, "", this.props.isTeamsPage ? Feature.Teams : undefined);
    }

    public componentDidUpdate() {
        if (this.props.deletedGroup) {
            onClickFPS(this.props.pageContext, this._url, false);
        }

        if (this.props.messageCardText !== "" && this.props.messageCardSeverity === MessageCardSeverity.Info) {
            sendToast(this.props.messageCardText, this.props.pageContext);
            this.props.onDismissMessageCard();
        }
    }

    public render(): JSX.Element {
        const imageUrl = this.props.currentGroupAvatarUrl || getAssetLocation(this.props.pageContext, teamImageUrl);

        return (
            <div className="flex-column flex-grow rhythm-vertical-16">
                {this.props.messageCardText !== "" && this.props.messageCardSeverity !== MessageCardSeverity.Info ? (
                    <div className="bolt-card">
                        <MessageCard severity={this.props.messageCardSeverity} onDismiss={() => this.props.onDismissMessageCard()}>
                            <span dangerouslySetInnerHTML={createMarkup(this.props.messageCardText)} />
                        </MessageCard>
                    </div>
                ) : null}
                {this.props.selectedGroup ? (
                    <SettingsGroupImageCard
                        disabled={this._editDisabled()}
                        onSave={(resetImage, imageData) => {
                            this._saveImageAction(resetImage, imageData);
                        }}
                        onLoadDisplayAvatarUrl={imageUrl}
                        pageContext={this.props.pageContext}
                        entityType={this.getType()}
                    />
                ) : null}
                <Card titleProps={{ text: format(Resources.GroupDetailsCardTitle, this.getType()), ariaLevel: 2 }}>
                    <div className="flex-column flex-grow rhythm-vertical-16">
                        <FormItem
                            label={format(Resources.GroupName, this.getType())}
                            message={this.newGroupNameErrorMessage.value}
                            error={this.newGroupNameErrorMessage.value !== ""}
                        >
                            <TextField
                                className="group-settings-textfield"
                                disabled={this._editDisabled()}
                                value={this.props.groupDisplayName}
                                onChange={this._onGroupNameEdit}
                            />
                        </FormItem>
                        <FormItem label={format(Resources.GroupDescription, this.getType())}>
                            <TextField
                                className="group-settings-textfield"
                                multiline={true}
                                disabled={this._editDisabled()}
                                value={this.props.groupDescription}
                                onChange={this._onGroupDescriptionEdit}
                                rows={5}
                            />
                        </FormItem>
                        <div className="flex-grow flex-row">
                            <Button
                                ariaLabel={Resources.SaveButton}
                                primary={true}
                                disabled={this._editDisabled() || !this.props.isDirty || this.newGroupNameErrorMessage.value != ""}
                                onClick={e => {
                                    this._onSaveChangesClick();
                                }}
                            >
                                {Resources.SaveButton}
                            </Button>
                        </div>
                        {this.props.isSaving ? (
                            <div className="flex flex-column flex-center justify-center overlay-spinner">
                                <Spinner size={SpinnerSize.large} label={Resources.SpinnerText} />
                            </div>
                        ) : null}
                    </div>
                </Card>
                {this.props.isTeamsPage && (
                    <SettingsAdminsCard
                        disabled={this._editAdminsDisabled()}
                        teamAdmins={this.props.teamAdmins}
                        removeAdmin={this._removeAdmin}
                        showAddAdminPanel={this.props.showAddAdminPanel}
                        pageContext={this.props.pageContext}
                        logTelemetry={this.props.logTelemetry}
                        onSaveMembers={this._onSaveMembers}
                    />
                )}
                <SettingsDeleteCard
                    groupName={this.props.groupDisplayName}
                    itemToDelete={this.props.selectedGroup}
                    onDeleteItemConfirm={(selectedItem: PermissionsGroup) => {
                        this.props.deleteGroupConfirm(selectedItem);
                    }}
                    disabled={this._deleteDisabled()}
                    entityType={this.getType()}
                />
                {this.props.isSaving ? (
                    <div className="flex flex-column flex-center justify-center overlay-spinner">
                        <Spinner size={SpinnerSize.large} label={Resources.SpinnerText} />
                    </div>
                ) : null}
            </div>
        );
    }

    private _onSaveMembers = (subjectIdentities: IIdentity[], adminAsMember: boolean) => {
        this.props.logTelemetry(
            GroupsComponent.GroupMembersPivot,
            "Save admin button clicked",
            {},
            "",
            this.props.isTeamsPage ? Feature.Teams : undefined
        );
        if (subjectIdentities.length > 0 && this.props.selectedGroup) {
            this.props.onSaveAdmins(subjectIdentities, this.props.selectedGroup, adminAsMember);
        }
    };

    private _deleteDisabled(): boolean {
        const { selectedGroup, isSaving } = this.props;
        return isSaving
            || (selectedGroup !== undefined
                && (selectedGroup.isDefaultTeam
                    || (selectedGroup.actionPermissions !== undefined && !selectedGroup.actionPermissions.isDeleteAllowed)));
    }

    private _editDisabled(): boolean {
        const { selectedGroup, isSaving } = this.props;
        return isSaving
            || (selectedGroup !== undefined
                && (selectedGroup.isAadGroup
                    || (selectedGroup.actionPermissions !== undefined && !selectedGroup.actionPermissions.isEditAllowed)));
    }

    private _editAdminsDisabled(): boolean {
        const { selectedGroup, isSaving } = this.props;
        return isSaving
            || (selectedGroup !== undefined && selectedGroup.actionPermissions !== undefined && !selectedGroup.actionPermissions.isEditAllowed);
    }

    private _onSaveChangesClick = (): void => {
        this._validateGroupName(this.props.groupDisplayName);

        if (this.newGroupNameErrorMessage.value == "" && this.props.selectedGroup && this.props.selectedGroup) {
            this.props.saveChangesClick(
                filterXSS(this.props.groupDisplayName),
                filterXSS(this.props.groupDescription),
                this.props.selectedGroup.descriptor
            );
        }
    };

    private _onGroupNameEdit = (event: any, groupDisplayName: string) => {
        this._validateGroupName(groupDisplayName);

        // We won't block you from EDITING the groupName to be wrong- we'll just block you from SAVING it like that.
        // Otherwise, you'll never see the error message explaining what you did wrong- it'll just eat your changes.
        this.props.groupNameEdit(groupDisplayName);
    };

    private _onGroupDescriptionEdit = (event: any, groupDescription: string) => {
        this.props.groupDescriptionEdit(groupDescription);
    };

    private async _saveImageAction(resetImage: boolean, imageData: ImageData) {
        if (this.props && this.props.selectedGroup && this.props.selectedGroup) {
            const groupDescriptor: string = this.props.selectedGroup.descriptor;
            this.props.saveImageConfirm(groupDescriptor, resetImage, imageData, {
                isAutoGenerated: false,
                size: AvatarSize.Large,
                timeStamp: new Date(),
                value: imageData.byteArray
            });
        }
    }

    private _validateGroupName(groupName: string) {
        groupName = groupName.trim();

        if (groupName === "") {
            this.newGroupNameErrorMessage.value = format(Resources.EmptyGroupNameError, this.getType());
        } else if (groupName.length > GroupNameMaxChar) {
            this.newGroupNameErrorMessage.value = format(Resources.GroupNameTooLargeError, this.getType(), GroupNameMaxChar);
        } else if (groupName.endsWith(".")) {
            this.newGroupNameErrorMessage.value = format(Resources.GroupNameEndingInPeriodError, this.getType());
        } else if (!!/[,"/\\[\]:|<>+=;?*@~'&\$#]/g.test(groupName)) {
            this.newGroupNameErrorMessage.value = format(Resources.IllegalCharactersNameError, this.getType());
        } else if (!!/[^ -~]+/g.test(groupName)) {
            this.newGroupNameErrorMessage.value = format(Resources.NonPrintableCharactersNameError, this.getType());
        } else if (
            groupName.toLowerCase() == IReservedGroupNames.Global.toLowerCase() ||
            groupName.toLowerCase() == IReservedGroupNames.AdministratorsGroup.toLowerCase() ||
            groupName.toLowerCase() == IReservedGroupNames.ServiceUsersGroup.toLowerCase() ||
            groupName.toLowerCase() == IReservedGroupNames.EveryoneGroup.toLowerCase()
        ) {
            this.newGroupNameErrorMessage.value = format(Resources.ReservedGroupNameError, this.getType());
        } else {
            this.newGroupNameErrorMessage.value = "";
        }
    }

    private getType(): string {
        return this.props.isTeamsPage ? Resources.Team : Resources.Group;
    }

    private _removeAdmin = (admin: TeamMember) => {
        if (this.props.selectedGroup) {
            this.props.removeAdmin(admin, this.props.selectedGroup);
        }
    };
}

function mapStateToProps(state: IGroupsViewState, providedProps: ISettingsPivotContainerProps): Partial<ISettingsPivotInitializerOwnProps> {
    return {
        ...providedProps,
        selectedGroup: GroupPageSelectors.getSelectedGroup(state),
        showChangeImageDialog: getShowChangeImageDialog(state),
        isSaving: getIsSaving(state),
        groupDisplayName: getGroupDisplayName(state),
        groupDescription: getGroupDescription(state),
        currentGroupAvatarUrl: getCurrentGroupAvatarUrl(state),
        isDirty: getIsDirty(state),
        messageCardText: getMessageCardText(state),
        messageCardSeverity: getMessageCardSeverity(state),
        deletedGroup: getDeletedGroup(state),
        isTeamsPage: GroupPageSelectors.getIsTeamsPageFlag(state),
        teamAdmins: getTeamAdmins(state),
        showAddAdminPanel: showAddAdminPanel(state)
    };
}

const ActionsToDispatch = {
    initView: SettingsPivotActions.initView,
    changeImageClick: SettingsPivotActions.changeImageClick,
    cancelImageClick: SettingsPivotActions.cancelImageClick,
    groupNameEdit: SettingsPivotActions.groupNameEdit,
    groupDescriptionEdit: SettingsPivotActions.groupDescriptionEdit,
    saveChangesClick: SettingsPivotActions.saveChangesClick,
    saveImageConfirm: SettingsPivotActions.saveImageConfirm,
    logTelemetry: GroupsViewActions.logTelemetry,
    deleteGroupConfirm: SettingsPivotActions.deleteGroupConfirm,
    onDismissMessageCard: SettingsPivotActions.onDismissMessageCard,
    removeAdmin: SettingsPivotActions.removeAdmin,
    openAddAdminPanel: SettingsPivotActions.openAddAdminPanel,
    closeAddAdminPanel: SettingsPivotActions.closeAddAdminPanel,
    onSaveAdmins: SettingsPivotActions.onSaveAdmins
};

export const ConnectedSettingsPivotInitializer = connect(mapStateToProps, ActionsToDispatch, null, { withRef: true })(SettingsPivotInitializer);
